import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { HistoryStore, ObservationHistory } from '@iotv/datamodel';
import { SortOrder } from '@iotv/iotv-v3-types';
import { Button, Grid, Stack, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { StrictMode, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppMainViewContext } from '../../ContextProviders/AppContextProvider';
import { useTabHandlerV2 } from '../../hooks/useTabHandler';
import { ActionIcons } from '../../Iconography';
import { AdjacentType } from '../../types/AppTypes';
import { DownloadButton } from '../common/ViewObjectDownloadButton';
import { IOType } from '../io/types';
import { SimpleIOModal } from '../io/VOBbased/SimpleIOModal';
import { Footerify } from '../utils/Footerify';
import { ObservationsContext } from './Contexts/OvesrvationsContextType_V2';
import { UseObservationsContext } from './Contexts/UseObservationsContext';
import dayjs from 'dayjs';
import { HistoryStoreContext } from '../../ContextProviders/HistoryStoreContext';
import { MatchedReadingsContext } from './Contexts/MatchedReadingsContext';
import { getObservationPeriod, getVideoStreamsContextsForEntity } from './fns';
import { metricItemsToVOBs } from './helperFns';
import { ManageKeys } from './Keys/ManageKeys';
import { MatchedReadingsDataGrid } from './Matching/MatchedReadings';
import { AddObservations } from './ObservationEntry/AddObservations_v1';
import { VideoMonitoringTab } from './Video/VideoMonitoringTab';
const debug = process.env.REACT_APP_DEBUG && false;
const dayjsFormat = 'YYYY-MM-DD HH:mm:ss';
export const ObservationCardInternal = (props) => {
    const navigate = useNavigate();
    const [{ isSmallView }] = useContext(AppMainViewContext);
    const { viewObject: viewObjectIn, objectHistory: objectHistoryIn, loadingObjectHistory } = props;
    const { matchedPrimary } = viewObjectIn;
    const { state: ObservationContextState, dispatch } = useContext(ObservationsContext);
    const { persistedObservationHistory, save, deleteObject, working, querying, errMessage } = useContext(UseObservationsContext);
    const { historyStoreFns: { setTimePeriod }, } = useContext(HistoryStoreContext);
    const { observationHistory, viewDefinition, objectHistory, googleSheet, matchedActualsCount, } = ObservationContextState;
    const { state: matchedReadingsState, dispatch: matchedReadingsDispatch } = useContext(MatchedReadingsContext);
    const observationHistoryStore = observationHistory?.observedHistory ?? null;
    debug && console.log('historyStore', observationHistoryStore);
    /* EXPERIMENTAL, get the spreadsheet and sheet names from useGoogleIO */
    const fileSelectRef = useRef(observationHistory?.googleSheet);
    const handleSave = () => {
        if (observationHistory) {
            debug && console.log('Save curried has fileselectref', fileSelectRef);
            const observationHistoryClone = { ...observationHistory };
            observationHistoryClone.googleSheet = fileSelectRef.current;
            const newObservationHistory = new ObservationHistory(observationHistoryClone);
            const action = { type: 'setObservationHistory', value: newObservationHistory };
            dispatch(action);
            save(newObservationHistory);
        }
        else {
            debug && console.log('No observationHistory to save');
        }
    };
    const handleDelete = () => {
        if (matchedPrimary) {
            const newObservationHistory = ObservationHistory.createForObject(matchedPrimary, {});
            newObservationHistory.observedHistory = new HistoryStore();
            const action = {
                type: 'setObservationHistory',
                value: newObservationHistory,
            };
            dispatch(action);
            deleteObject();
        }
    };
    const fetchVideoHubContexts = async () => {
        if (matchedPrimary) {
            const videoHubContexts = await getVideoStreamsContextsForEntity(matchedPrimary);
            const action = { type: 'setMatchedVideoHubContexts', value: videoHubContexts };
            dispatch(action);
        }
    };
    useEffect(() => {
        if (matchedPrimary) {
            fetchVideoHubContexts();
        }
    }, [matchedPrimary?.sk]);
    useEffect(() => {
        if (persistedObservationHistory) {
            debug && console.log('re rendering on changed persistedObservationHistory', persistedObservationHistory);
            const action = { type: 'setObservationHistory', value: persistedObservationHistory };
            dispatch(action);
            if (persistedObservationHistory.googleSheet) {
                fileSelectRef.current = persistedObservationHistory.googleSheet;
                console.log('TEMP Set fileSelectRef from persistedObservationHistory on load', {
                    fileSelectRef: fileSelectRef.current,
                    persistedGoogleSheet: persistedObservationHistory.googleSheet,
                });
            }
            const timePeriod = getObservationPeriod(persistedObservationHistory);
            if (timePeriod) {
                setTimePeriod({ fromDateValue: dayjs(timePeriod.fromTimestampMs), toDateValue: dayjs(timePeriod.toTimestampMs) });
            }
        }
    }, [persistedObservationHistory]);
    useEffect(() => {
        debug && console.log('re rendering on changed objectHistory');
        if (objectHistoryIn) {
            const action = {
                type: 'setObjectHistory',
                value: objectHistoryIn,
            };
            dispatch(action);
        }
    }, [objectHistoryIn]);
    const observatinStateSnapshots = observationHistoryStore ? observationHistoryStore.getStateSnapshotArray(SortOrder.DESCENDING) : [];
    const pseudoTypeId = `HistoryStore_observation`;
    const observationMetricItems = observationHistory
        ? metricItemsToVOBs({
            psuedoParentVob: observationHistory,
            pseudoTypeId,
            stateSnapshots: observatinStateSnapshots,
        })
        : [];
    const viewObject = observationHistory
        ? {
            matchedPrimary: observationHistory,
            matchedRelatedByPk: [],
            matchedRelatedBySk: observationMetricItems,
        }
        : null;
    const adjacentFilter = {
        adjacentType: AdjacentType.CHILD,
        objectType: pseudoTypeId,
    };
    const secondLevelTabs = {
        'Add Observations': () => _jsx(AddObservations, {}),
        'Manage Keys': () => _jsx(ManageKeys, {}),
        'Matched Readings': () => _jsx(MatchedReadingsDataGrid, {}),
        'Video': () => _jsx(VideoMonitoringTab, {}),
    };
    const { tabs } = useTabHandlerV2({
        tabsToDisplay: secondLevelTabs,
        initialTabName: 'AddObservations',
        level: 2,
        returnTabs: true,
    });
    const pk = observationHistory?.sk ?? 'unknownParentSk';
    const saveButton = (_jsx(Button, { variant: 'contained', startIcon: _jsx(ActionIcons.Save, {}), onClick: handleSave, disabled: working, children: "Save" }, `${pk}_observationHistoryCard_SaveButton`));
    const deleteButton = (_jsx(Button, { variant: 'contained', startIcon: _jsx(ActionIcons.Delete, {}), onClick: handleDelete, disabled: working, children: "Delete" }, `${pk}_observationHistoryCard_DeleteButton`));
    const downloadButton = viewDefinition && viewObject ? (_jsx(DownloadButton, { viewObject, viewDefinition, adjacentFilter }, `${pk}_observationHistoryCard_DownloadButton`)) : null;
    const setPlainObjectsInParent = (observations) => {
        // not used
    };
    const { keys, vobs } = useMemo(() => {
        const { bucketedHistory, matchedHistory } = matchedReadingsState;
        if (bucketedHistory) {
            const bucketedKeys = bucketedHistory?.keys();
            const exportVobs = bucketedHistory.getStateSnapshotArray(SortOrder.ASCENDING);
            const augmentedVobs = exportVobs.map((snapshot, i) => {
                const nextTimestamp = i < exportVobs.length - 1 ? exportVobs[i + 1].timestamp : null;
                const timestamp = snapshot.timestamp;
                const from = dayjs(timestamp).format(dayjsFormat);
                const to = nextTimestamp ? dayjs(nextTimestamp).format(dayjsFormat) : null;
                const from_with_buffer = dayjs(timestamp).subtract(matchedReadingsState.bufferMins, 'minute').format(dayjsFormat);
                const to_with_buffer = nextTimestamp ? dayjs(nextTimestamp).add(matchedReadingsState.bufferMins, 'minute').format(dayjsFormat) : null;
                const state = snapshot.state;
                return {
                    from, to, from_with_buffer, to_with_buffer,
                    ...state,
                    id: `${timestamp}`,
                    type: 'bucketed_observation',
                };
            });
            const allKeys = ['from', 'to', 'from_with_buffer', 'to_with_buffer', ...bucketedKeys ?? []];
            return { keys: allKeys, vobs: augmentedVobs };
        }
        else if (matchedHistory) {
            const matchedKeys = matchedHistory.keys().filter((k) => !['fromTimestampMs', 'toTimestampMs',].includes(k));
            const exportVobs = matchedHistory.getStateSnapshotArray(SortOrder.ASCENDING).map((snapshot) => {
                const { fromTimestampMs, toTimestampMs, ...otherState } = snapshot.state;
                const from = dayjs(fromTimestampMs).format(dayjsFormat);
                const to = dayjs(toTimestampMs).format(dayjsFormat);
                const from_with_buffer = dayjs(fromTimestampMs).subtract(matchedReadingsState.bufferMins, 'minute').format(dayjsFormat);
                const to_with_buffer = dayjs(toTimestampMs).add(matchedReadingsState.bufferMins, 'minute').format(dayjsFormat);
                return {
                    from, to, from_with_buffer, to_with_buffer,
                    ...otherState,
                    id: `${fromTimestampMs}_${toTimestampMs}`,
                    type: 'matched_observation',
                };
            });
            const allKeys = ['from', 'to', 'from_with_buffer', 'to_with_buffer', ...matchedKeys ?? []];
            return { keys: allKeys, vobs: exportVobs };
        }
        else {
            return { keys: [], vobs: [] };
        }
    }, [matchedReadingsState]);
    return (_jsx(StrictMode, { children: _jsxs(LocalizationProvider, { dateAdapter: AdapterDayjs, children: [_jsx(Stack, { marginBlockStart: -1, maxHeight: '80vh', children: _jsx(Stack, { direction: 'column', gap: 2, width: '100%', children: tabs }, "ObsStack") }, "wrapper"), _jsxs(Footerify, { children: [_jsxs(Stack, { direction: 'row', gap: 1, children: [saveButton, deleteButton, _jsx(SimpleIOModal, { columnIndexedKeys: keys,
                                    plainObjects: vobs,
                                    allowIOTypeToggle: false,
                                    initialIOType: IOType.EXPORT,
                                    setPlainObjectsInParent,
                                    sheets: [{ sheetName: `${viewObject?.matchedPrimary?.name} Observations`, headers: keys }],
                                    googleSheet }, 'SimpleIOModal'), downloadButton] }, "ObsIO"), _jsxs(Grid, { container: true, children: [_jsx(Grid, { size: { md: 12, xl: 6 }, children: googleSheet && _jsx(Typography, { children: `last sheet: ${googleSheet.spreadsheetName}` }) }, 'ExportGrid'), _jsx(Grid, {}, 'download')] }, 'Export')] }, 'ObservationCardFooter')] }) }, "ObsStick"));
};
